import React, { useEffect, useRef, useState } from 'react';
import './Sheet.css';

/**
 * Bottom sheet — the mobile stand-in for a modal.
 * Slides up from the bottom, dims the page, closes on backdrop tap,
 * on Escape, or by dragging the grabber down.
 */
export default function Sheet({ open, onClose, title, subtitle, children, footer, size = 'auto' }) {
    const [mounted, setMounted] = useState(open);
    const [closing, setClosing] = useState(false);
    const [dragY, setDragY] = useState(0);
    const startY = useRef(null);
    const panelRef = useRef(null);

    useEffect(() => {
        if (open) {
            setMounted(true);
            setClosing(false);
            setDragY(0);
        } else if (mounted) {
            setClosing(true);
            const t = setTimeout(() => setMounted(false), 220);
            return () => clearTimeout(t);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Lock the page behind the sheet
    useEffect(() => {
        if (!mounted) return;
        document.body.classList.add('sheet-open');
        return () => document.body.classList.remove('sheet-open');
    }, [mounted]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => e.key === 'Escape' && onClose && onClose();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!mounted) return null;

    const onTouchStart = (e) => { startY.current = e.touches[0].clientY; };
    const onTouchMove = (e) => {
        if (startY.current == null) return;
        const dy = e.touches[0].clientY - startY.current;
        if (dy > 0) setDragY(dy);
    };
    const onTouchEnd = () => {
        if (dragY > 110) {
            if (onClose) onClose();
        } else {
            setDragY(0);
        }
        startY.current = null;
    };

    return (
        <div className={`sheet-root ${closing ? 'closing' : ''}`}>
            <div className="sheet-backdrop" onClick={onClose} />

            <div
                ref={panelRef}
                className={`sheet-panel size-${size}`}
                style={dragY ? { transform: `translateY(${dragY}px)`, transition: 'none' } : undefined}
                role="dialog"
                aria-modal="true"
            >
                <div
                    className="sheet-grab-area"
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                >
                    <span className="sheet-grabber" />
                </div>

                {(title || subtitle) && (
                    <div className="sheet-head">
                        <div className="sheet-head-text">
                            {subtitle && <div className="sheet-sub">{subtitle}</div>}
                            {title && <h3 className="sheet-title">{title}</h3>}
                        </div>
                        <button type="button" className="icon-btn" aria-label="Đóng" onClick={onClose}>
                            <i className="fa-solid fa-xmark"></i>
                        </button>
                    </div>
                )}

                <div className="sheet-body">{children}</div>

                {footer && <div className="sheet-footer">{footer}</div>}
            </div>
        </div>
    );
}
